import { useEffect, useMemo, useState } from 'react';
import { MdExpandMore, MdOutlineImage } from 'react-icons/md';

import { imageVariantLink, type ImageFile } from '@/common/media';
import { DateTimePicker, Modal, SegmentedControl } from '@/components';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { ButtonOutlined } from '@/components/Button';
import { Input, LOGO_PICKER_HEIGHT, LOGO_PICKER_WIDTH, LogoPicker } from '@/components/Inputs';
import OutOfWaterBadge from '@/components/OutOfWaterBadge/OutOfWaterBadge';
import { RichTextArea } from '@/components/RichTextArea/RichTextArea';

import { AiGenerating } from '../AiGenerating';
import type { AiCover, OutOfWaterRange, WizardKind } from '../eventWizard.types';
import { fmtDate, fromIsoValue, toIsoValue } from '../eventWizard.utils';
import s from '../EventWizardModal.module.scss';

interface StepDetailsProps {
  title: string;
  onTitleChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  isTrip: boolean;
  /** False when the caller pinned the kind (the Plan page's Add-trip buttons). */
  showKindToggle: boolean;
  onKindChange: (kind: WizardKind) => void;
  startDate: Date | null;
  endDate: Date | null;
  allDay: boolean;
  onAllDayChange: (checked: boolean) => void;
  onDatesChange: (start: Date | null, end: Date | null) => void;
  // exceedsMaxDays: boolean;
  outOfWaterRange: OutOfWaterRange | null;
  moreDetailsOpen: boolean;
  onToggleMoreDetails: () => void;
  photo: File | null;
  onPhotoChange: (photo: File | null) => void;
  pickerActive: boolean;
  onPickerActiveChange: (active: boolean) => void;
  logoPickerRef: (input: HTMLInputElement | null) => void;
  onOpenFilePicker: () => void;
  aiCover: AiCover | null;
  onRemoveAiCover: () => void;
  aiCoverLoading: boolean;
  aiCoverError: string | null;
  onGenerateAiCover: () => void;
  /** An existing event's stored cover, shown until another one replaces it. */
  existingCover?: ImageFile | null;
  /**
   * Set when editing an existing event: a picked cover is saved on Done through this rather
   * than with the wizard's Save. The backend cannot delete a cover, so Remove is not offered.
   */
  saveCover?: (file: File) => Promise<unknown>;
}

const KIND_OPTIONS: readonly { label: string; value: WizardKind }[] = [
  { label: '📅 Event', value: 'event' },
  { label: '💡 Idea', value: 'trip' },
];

export function StepDetails({
  title,
  onTitleChange,
  description,
  onDescriptionChange,
  isTrip,
  showKindToggle,
  onKindChange,
  startDate,
  endDate,
  allDay,
  onAllDayChange,
  onDatesChange,
  // exceedsMaxDays,
  outOfWaterRange,
  moreDetailsOpen,
  onToggleMoreDetails,
  photo,
  onPhotoChange,
  pickerActive,
  onPickerActiveChange,
  logoPickerRef,
  onOpenFilePicker,
  aiCover,
  onRemoveAiCover,
  aiCoverLoading,
  aiCoverError,
  onGenerateAiCover,
  existingCover,
  saveCover,
}: StepDetailsProps) {
  const today = useMemo(() => new Date(), []);
  // const maxEndDate = startDate ? addDays(startDate, MAX_EVENT_DAYS - 1) : undefined;
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);

  useEffect(() => {
    if (!photoUrl) return;
    return () => URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);

  const aiCoverSrc = aiCover ? `data:${aiCover.contentType};base64,${aiCover.base64}` : null;

  const replacePhoto = () => {
    onPhotoChange(null);
    onPickerActiveChange(true);
    onOpenFilePicker();
  };

  return (
    <>
      <div className={s.stepHeader}>
        <h3 className={s.stepTitle}>Let’s start with the basics.</h3>
        <p className={s.stepSubtitle}>
          {isTrip
            ? 'A name is all you need. Dates and everything else can come later.'
            : 'Please add a name and date. Everything else is optional.'}
        </p>
      </div>
      <div className={s.fields}>
        {showKindToggle && (
          <SegmentedControl<WizardKind>
            className={s.kindToggle}
            options={KIND_OPTIONS}
            value={isTrip ? 'trip' : 'event'}
            onChange={onKindChange}
          />
        )}
        <div className={s.photoUpload}>
          {aiCoverLoading && (
            <AiGenerating hint="Designing a cover from your event title" lines={0} />
          )}
          {!aiCoverLoading && aiCoverSrc && (
            <div className={s.aiCoverPreview}>
              <button
                type="button"
                className={s.coverThumbBtn}
                onClick={() => setPreviewSrc(aiCoverSrc)}
                aria-label="Preview AI-generated cover"
              >
                <img src={aiCoverSrc} alt="AI-generated event cover" />
              </button>
              <div className={s.aiCoverMeta}>
                <span className={s.aiCoverBadge}>✨ AI cover</span>
                <span className={s.aiCoverCaption}>Based on “{title.trim() || 'your event'}”</span>
              </div>
              <div className={s.aiCoverActions}>
                <ButtonOutlined onClick={onGenerateAiCover}>Regenerate</ButtonOutlined>
                {!saveCover && <ButtonOutlined onClick={onRemoveAiCover}>Remove</ButtonOutlined>}
              </div>
            </div>
          )}
          {!aiCoverLoading && aiCoverError && (
            <span className={s.aiCoverErrorText}>{aiCoverError}</span>
          )}
          {!aiCoverLoading && !aiCover && !photo && !pickerActive && existingCover && (
            <div className={s.aiCoverPreview}>
              <button
                type="button"
                className={s.coverThumbBtn}
                onClick={() => setPreviewSrc(existingCover.link ?? null)}
                aria-label="Preview current cover"
              >
                <AuthorizedImage
                  src={imageVariantLink(existingCover, 120, 68)}
                  alt="Current event cover"
                />
              </button>
              <div className={s.aiCoverMeta}>
                <span className={s.aiCoverBadge}>My Cover</span>
                <span className={s.aiCoverCaption}>Click the image to see it full size.</span>
              </div>
              <div className={s.aiCoverActions}>
                <ButtonOutlined onClick={replacePhoto}>Replace</ButtonOutlined>
                {title.trim() && (
                  <ButtonOutlined onClick={onGenerateAiCover}>Generate with AI</ButtonOutlined>
                )}
              </div>
            </div>
          )}
          {!aiCoverLoading && !aiCover && !photo && !pickerActive && !existingCover && (
            <div className={s.coverSlot}>
              <button type="button" className={s.coverSlotBtn} onClick={onOpenFilePicker}>
                <MdOutlineImage />
                <span>Add Cover</span>
              </button>
              {title.trim() && (
                <>
                  <span className={s.coverSlotSep}>or</span>
                  <button type="button" className={s.coverSlotBtn} onClick={onGenerateAiCover}>
                    <span className={s.coverSlotSparkle}>✨</span>
                    <span>Generate with AI</span>
                  </button>
                </>
              )}
            </div>
          )}
          <div className={pickerActive && !photo ? undefined : s.hidden}>
            <LogoPicker
              onChange={(img) => {
                const keep = () => {
                  onPhotoChange(img);
                  onPickerActiveChange(false);
                };
                // Editing: the cover is saved now, and shown as the cover once it is in.
                if (!saveCover) return keep();
                // A failed save goes back to the stored cover, which is still the cover.
                return saveCover(img).then(keep, (error: unknown) => {
                  onPickerActiveChange(false);
                  throw error;
                });
              }}
              onCancel={() => {
                onPhotoChange(null);
                onPickerActiveChange(false);
              }}
              initialLogo={
                saveCover
                  ? imageVariantLink(existingCover, LOGO_PICKER_WIDTH, LOGO_PICKER_HEIGHT)
                  : undefined
              }
              round={false}
              ref={logoPickerRef}
            />
          </div>
          {photoUrl && (
            <div className={s.aiCoverPreview}>
              <button
                type="button"
                className={s.coverThumbBtn}
                onClick={() => setPreviewSrc(photoUrl)}
                aria-label="Preview uploaded cover"
              >
                <img src={photoUrl} alt="Uploaded event cover" />
              </button>
              <div className={s.aiCoverMeta}>
                <span className={s.aiCoverBadge}>My Cover</span>
                <span className={s.aiCoverCaption}>Click the image to see it full size.</span>
              </div>
              <div className={s.aiCoverActions}>
                <ButtonOutlined onClick={replacePhoto}>Replace</ButtonOutlined>
                {!saveCover && (
                  <ButtonOutlined
                    onClick={() => {
                      onPhotoChange(null);
                      onPickerActiveChange(false);
                    }}
                  >
                    Remove
                  </ButtonOutlined>
                )}
              </div>
            </div>
          )}
        </div>
        <div className={s.detailsCard}>
          <Input
            label={isTrip ? 'Trip Title' : 'Event Title'}
            mandatory
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder={isTrip ? 'Amalfi Coast in July' : 'Birthday Celebration at Cannes'}
            maxLength={100}
          />
          <DateTimePicker
            label={isTrip ? 'Trip Dates' : 'Dates'}
            placeholder="Choose dates"
            mandatory={!isTrip}
            allDay={allDay}
            onAllDayChange={onAllDayChange}
            minDate={today}
            startLabel={isTrip ? 'Departs' : 'Starts'}
            endLabel={isTrip ? 'Returns' : 'Ends'}
            value={toIsoValue(startDate)}
            endValue={toIsoValue(endDate)}
            onRangeChange={(start, end) =>
              onDatesChange(start ? fromIsoValue(start) : null, end ? fromIsoValue(end) : null)
            }
          />
          {isTrip && !startDate && (
            <span className={s.dateHint}>Leave the dates empty to save this as an idea.</span>
          )}
          {/*{exceedsMaxDays && (*/}
          {/*  <span className={s.aiCoverErrorText}>*/}
          {/*    An event can span at most {MAX_EVENT_DAYS} days.*/}
          {/*  </span>*/}
          {/*)}*/}
          {outOfWaterRange && (
            <div className={s.owCard}>
              <OutOfWaterBadge align="left" size="small" variant="dark" />
              <span className={s.owText}>
                This vessel is scheduled to be out of water {fmtDate(outOfWaterRange.start)} –{' '}
                {fmtDate(outOfWaterRange.end)}.
              </span>
            </div>
          )}
          <div className={s.descField}>
            <button
              type="button"
              className={s.descToggle}
              onClick={onToggleMoreDetails}
              aria-expanded={moreDetailsOpen}
            >
              <span className={s.descLabel}>More Details</span>
              <MdExpandMore
                className={`${s.descChevron} ${moreDetailsOpen ? s.descChevronOpen : ''}`}
              />
            </button>
            {moreDetailsOpen && (
              <div className={s.moreDetails}>
                <RichTextArea
                  label="Description"
                  value={description}
                  onChange={onDescriptionChange}
                  placeholder="Champagne on the aft deck at sunset"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal open={!!previewSrc} onClose={() => setPreviewSrc(null)} title="Cover Preview">
        <div className={s.coverPreviewBody}>
          {previewSrc && (
            <AuthorizedImage
              className={s.coverPreviewImage}
              src={previewSrc}
              alt="Event cover preview"
            />
          )}
        </div>
      </Modal>
    </>
  );
}
